// Sealed pictures (M72, docs/sealed-pictures.md). People's, families' and
// groups' pictures are sealed on the device; the server keeps opaque text
// under `/api/avatars/<hash>` (D293) and can't see a face. A device opens a
// picture once per visit into an object URL (D294). Anything that can't be
// opened, for any reason, is null and the caller shows initials: a missing
// face is acceptable, a wrong one never is.
import { FAMILY_LIMITS } from '@twinden/shared';
import { api } from '../api';
import { fromBase64, messageAad, openEnvelope, toBase64 } from './crypto';
import { groupAadV2 } from './envelopeV2';
import { KeysNotReadyError } from './groupInfo';
import {
  e2eeUserId,
  isE2EEReady,
  admitGroupRevision,
  keyFor,
  nextGroupRevision,
  noteV2Opened,
  onKeysArrivedQuietly,
  onScopeKeysArrived,
  openAppRecord,
  seal,
  sealApp,
  v1Allowed,
  writeSealedApp,
  type Envelope,
} from './session';

const subtle = () => globalThis.crypto.subtle;
const encoder = new TextEncoder();
const decoder = new TextDecoder();

const ADDRESS = /^\/api\/avatars\/([0-9a-f]{32})$/;
// Only the image types the server ever let in; never SVG.
const IMAGE_TYPE = /^image\/(?:jpeg|png|gif|webp)$/;
const DATA_URL =
  /^data:(image\/(?:jpeg|png|gif|webp));base64,([A-Za-z0-9+/]+={0,2})$/;
const SEALED_APP = 'e2ee:v1:';

const userAad = (userId: string) => `twinden picture v1|user|${userId}`;

/** The sealed picture is over the server's limit (a smaller one will do). */
export class PictureTooLargeError extends Error {
  constructor() {
    super('PICTURE_TOO_LARGE');
  }
}

function checkSize(sealed: string) {
  if (sealed.length > FAMILY_LIMITS.MAX_SEALED_PICTURE_LENGTH) {
    throw new PictureTooLargeError();
  }
}

async function sha256Hex32(text: string): Promise<string> {
  const digest = new Uint8Array(
    await subtle().digest('SHA-256', encoder.encode(text))
  );
  return Array.from(digest, (byte) => byte.toString(16).padStart(2, '0'))
    .join('')
    .slice(0, 32);
}

/**
 * The address hash of a sealed picture, as the server makes it
 * (`server/src/utils/avatars.ts`): the first 32 hex characters of SHA-256
 * over the sealed text's UTF-8. Computed here so a new picture and its
 * wrapped keys go up in one write (D292).
 */
export function pictureHash(sealed: string): Promise<string> {
  return sha256Hex32(sealed);
}

function splitDataUrl(dataUrl: string): { mime: string; data: string } {
  const match = DATA_URL.exec(dataUrl);
  if (!match) throw new Error('Not a picture');
  return { mime: match[1]!, data: match[2]! };
}

// ---- A person's picture (D292) ---------------------------------------------
//
// The format, shared with Android and iOS byte for byte
// (shared/contracts/sealed-picture-vector.json): the sealed text is standard
// base64 (with padding) of the UTF-8 JSON
//   {"v":1,"kind":"user","userId":"…","iv":"…","data":"…"}
// in exactly that field order, no spaces. `iv` is 12 bytes, standard base64;
// `data` is standard base64 of AES-256-GCM over the UTF-8 data URL
// (ciphertext then the 16-byte tag), AAD `twinden picture v1|user|<userId>`.
// The key goes to each family as a `pkey` record `{ userId, pictureHash,
// key }`, `key` being standard base64 of the raw 32 bytes.

async function sealUserRecord(
  userId: string,
  dataUrl: string,
  key: CryptoKey,
  iv: Uint8Array<ArrayBuffer>
): Promise<string> {
  splitDataUrl(dataUrl);
  const data = await subtle().encrypt(
    { name: 'AES-GCM', iv, additionalData: encoder.encode(userAad(userId)) },
    key,
    encoder.encode(dataUrl)
  );
  const record = JSON.stringify({
    v: 1,
    kind: 'user',
    userId,
    iv: toBase64(iv),
    data: toBase64(data),
  });
  return toBase64(encoder.encode(record));
}

/**
 * Seals with a fixed key and iv, for the shared vector test only; the app
 * uses sealUserPicture (a fresh key and iv every time).
 * @internal
 */
export const sealUserPictureForVectorTest = sealUserRecord;

interface UserRecord {
  v: 1;
  kind: 'user';
  userId: string;
  iv: string;
  data: string;
}

function readUserRecord(sealed: string): UserRecord | null {
  try {
    const record = JSON.parse(decoder.decode(fromBase64(sealed))) as unknown;
    if (!record || typeof record !== 'object') return null;
    const { v, kind, userId, iv, data } = record as Record<string, unknown>;
    if (v !== 1 || kind !== 'user') return null;
    if (typeof userId !== 'string' || typeof iv !== 'string') return null;
    if (typeof data !== 'string') return null;
    return { v, kind, userId, iv, data };
  } catch {
    return null;
  }
}

/**
 * Opens a person's sealed picture with its key: the data URL, or null. The
 * AAD is the userId the record names, so a blob relabelled as someone
 * else's never opens.
 */
export async function openUserPictureWith(
  sealed: string,
  key: CryptoKey
): Promise<string | null> {
  const record = readUserRecord(sealed);
  if (!record) return null;
  try {
    const plain = await subtle().decrypt(
      {
        name: 'AES-GCM',
        iv: fromBase64(record.iv),
        additionalData: encoder.encode(userAad(record.userId)),
      },
      key,
      fromBase64(record.data)
    );
    const dataUrl = decoder.decode(plain);
    return DATA_URL.test(dataUrl) ? dataUrl : null;
  } catch {
    return null;
  }
}

/**
 * Seals my picture once with a fresh picture key and wraps that key for
 * each family (D292). The caller sends `{ sealed, keys }` in one PATCH of
 * `/users/me`. A family whose key can't be had right now is left out; it's
 * wrapped later (`wrapPictureKey`, D295). Throws PictureTooLargeError over
 * the server's limit, KeysNotReadyError without this device's key.
 */
export async function sealUserPicture(
  userId: string,
  dataUrl: string,
  familyIds: string[]
): Promise<{
  sealed: string;
  keys: Array<{ familyId: string; sealed: string }>;
  pictureHash: string;
}> {
  // Without this device's key no family could open it (D55).
  if (!isE2EEReady()) throw new KeysNotReadyError();
  // Extractable only to take its raw bytes for the families' records.
  const sealing = await subtle().generateKey(
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt']
  );
  const iv = globalThis.crypto.getRandomValues(
    new Uint8Array(new ArrayBuffer(12))
  );
  const sealed = await sealUserRecord(userId, dataUrl, sealing, iv);
  checkSize(sealed);
  const hash = await pictureHash(sealed);
  const raw = toBase64(await subtle().exportKey('raw', sealing));
  const key = await importPictureKey(raw);
  const mine = userId === e2eeUserId();
  if (mine) ownKeys.set(hash, raw);
  const keys: Array<{ familyId: string; sealed: string }> = [];
  for (const familyId of familyIds) {
    try {
      const wrapped = await sealApp(familyId, 'pkey', {
        userId,
        pictureHash: hash,
        key: raw,
      });
      keys.push({ familyId, sealed: wrapped });
      // Sent with the picture, so the upkeep pass needn't wrap it again.
      if (mine) {
        ownRows.set(familyId, {
          pictureHash: hash,
          epoch: sealedEpoch(wrapped),
        });
      }
      if (key) {
        remember(familyId, userId, {
          pictureHash: hash,
          raw: mine ? raw : null,
          key,
        });
      }
    } catch {
      // No family key here yet, or offline: wrapped on a later pass (D295).
    }
  }
  if (keys.length > 0) notify();
  return { sealed, keys, pictureHash: hash };
}

/**
 * Wraps my current picture's key for one more family, or again after its
 * key rotated (D295): the key must already be open here through another
 * family, or sealed here this visit. A sealed family write, so a pending
 * rotation runs first. false when this device hasn't got the key.
 */
export async function wrapPictureKey(
  familyId: string,
  userId: string,
  hash: string
): Promise<boolean> {
  if (userId !== e2eeUserId()) return false;
  let raw = ownKeys.get(hash);
  for (const users of pictureKeys.values()) {
    const known = users.get(userId);
    if (!raw && known?.pictureHash === hash && known.raw) raw = known.raw;
  }
  if (!raw) return false;
  const wrapped = raw;
  let epoch = 0;
  await writeSealedApp(
    familyId,
    'pkey',
    { userId, pictureHash: hash, key: wrapped },
    (sealed) => {
      epoch = sealedEpoch(sealed);
      return api.putPictureKey(familyId, { pictureHash: hash, sealed });
    }
  );
  ownRows.set(familyId, { pictureHash: hash, epoch });
  const key = await importPictureKey(wrapped);
  if (key) remember(familyId, userId, { pictureHash: hash, raw: wrapped, key });
  return true;
}

/** The family-key epoch a sealed app record names (`e2ee:v1:<f>:<epoch>:…`). */
function sealedEpoch(sealed: string): number {
  const epoch = Number(sealed.split(':')[3]);
  return Number.isInteger(epoch) ? epoch : 0;
}

/**
 * My own picture-key row in a family, as the server lists it (D295):
 * which picture it's for and the family-key epoch it's sealed under, null
 * when there's none, undefined until that family's keys have been read.
 * Kept whether or not this device can open it, so a row it can't open
 * isn't taken for a missing one.
 */
export function myPictureKey(
  familyId: string
): { pictureHash: string; epoch: number } | null | undefined {
  if (!loadedFamilies.has(familyId)) return undefined;
  return ownRows.get(familyId) ?? null;
}

/**
 * Whether the picture at `address` is still readable (from before D297) or
 * sealed: one fetch, shared with opening faces (it's kept for the visit).
 * A readable one comes back as its data URL, to be sealed. null when it
 * can't be told now (offline, or not what its address names).
 */
export async function pictureForm(
  address: string
): Promise<{ readable: string } | { sealed: true } | null> {
  if (DATA_URL.test(address)) return { readable: address };
  const match = ADDRESS.exec(address);
  if (!match) return null;
  const got = await fetchPicture(address, match[1]!);
  if (!got) return null;
  return 'url' in got ? { readable: got.url } : { sealed: true };
}

// ---- Family and group pictures (D291) --------------------------------------

/** A family's picture, as a `picture` record sealed with the family key. */
export async function sealFamilyPicture(
  familyId: string,
  dataUrl: string
): Promise<string> {
  const sealed = await sealApp(familyId, 'picture', {
    v: 1,
    picture: splitDataUrl(dataUrl),
  });
  checkSize(sealed);
  return sealed;
}

/**
 * A group's picture, sealed with the chat's key like its description
 * (D234): AAD bound to the chat and a fresh client id.
 */
export async function sealGroupPicture(
  conversationId: string,
  dataUrl: string,
  /** The server's revision after STALE_REVISION (D326). */
  atLeast = 0
): Promise<{
  content: string;
  keyEpoch: number;
  clientId: string;
  encryption: 'v2';
  revision: number;
}> {
  const picture = splitDataUrl(dataUrl);
  const clientId = crypto.randomUUID();
  const revision = nextGroupRevision(conversationId, 'picture', atLeast);
  const sealed = await seal(
    conversationId,
    clientId,
    { v: 1, picture },
    { group: 'picture', revision }
  );
  checkSize(sealed.content);
  return { ...sealed, clientId, revision };
}

function pictureDataUrl(envelope: unknown): string | null {
  const picture = (envelope as { picture?: unknown } | null)?.picture as
    | { mime?: unknown; data?: unknown }
    | undefined;
  if (typeof picture?.mime !== 'string' || typeof picture.data !== 'string')
    return null;
  const dataUrl = `data:${picture.mime};base64,${picture.data}`;
  return DATA_URL.test(dataUrl) ? dataUrl : null;
}

// ---- Picture keys (D292) ---------------------------------------------------

interface PictureKey {
  pictureHash: string;
  /** The raw bytes, kept for my own picture only (to wrap it, D295). */
  raw: string | null;
  /** Non-extractable; opens only. */
  key: CryptoKey;
}

// family → user → the key that opens that user's current picture there.
const pictureKeys = new Map<string, Map<string, PictureKey>>();
// My pictures sealed on this device this visit (hash → raw), so a family
// left out at sealing can still be wrapped for (D295).
const ownKeys = new Map<string, string>();
const loadedFamilies = new Set<string>();
// family → my own row there, opened or not (D295).
const ownRows = new Map<string, { pictureHash: string; epoch: number }>();
// Reads under way, per family, so two screens asking at once share one.
const refreshing = new Map<string, Promise<boolean>>();
const rereads = new Map<string, Promise<boolean>>();
// Bumped on forgetting, so a read that started before can't write after.
let generation = 0;

function remember(familyId: string, userId: string, entry: PictureKey) {
  let users = pictureKeys.get(familyId);
  if (!users) {
    users = new Map();
    pictureKeys.set(familyId, users);
  }
  users.set(userId, entry);
}

async function importPictureKey(raw: string): Promise<CryptoKey | null> {
  try {
    const bytes = fromBase64(raw);
    if (bytes.length !== 32) return null;
    return await subtle().importKey('raw', bytes, 'AES-GCM', false, [
      'decrypt',
    ]);
  } catch {
    return null;
  }
}

/** Whether any family here still has this user's picture at this hash. */
function stillCurrent(userId: string, hash: string): boolean {
  for (const users of pictureKeys.values()) {
    if (users.get(userId)?.pictureHash === hash) return true;
  }
  return false;
}

/** Lets go of opened faces that are no longer anyone's current picture. */
function dropStale(replaced: Array<{ userId: string; hash: string }>) {
  for (const { userId, hash } of replaced) {
    if (stillCurrent(userId, hash)) continue;
    const address = `/api/avatars/${hash}`;
    const entry = opened.get(address);
    if (entry?.kind === 'user' && entry.owner === userId) {
      URL.revokeObjectURL(entry.url);
      opened.delete(address);
      fetched.delete(address);
    }
  }
}

/**
 * Reads a family's picture keys again; true when something changed. A read
 * already under way is shared; `fresh` waits for it and reads once more
 * (something changed after it started: a key arrived, a picture moved).
 */
function refreshKeys(familyId: string, fresh = false): Promise<boolean> {
  const running = refreshing.get(familyId);
  if (running && !fresh) return running;
  if (running) {
    const queued = rereads.get(familyId);
    if (queued) return queued;
    const next = running.then(() => {
      rereads.delete(familyId);
      return refreshKeys(familyId);
    });
    rereads.set(familyId, next);
    return next;
  }
  const request = readKeys(familyId).finally(() => refreshing.delete(familyId));
  refreshing.set(familyId, request);
  return request;
}

async function readKeys(familyId: string): Promise<boolean> {
  const started = generation;
  let rows: Array<{ userId: string; pictureHash: string; sealed: string }>;
  try {
    rows = (await api.getPictureKeys(familyId)).data;
  } catch {
    return false;
  }
  const me = e2eeUserId();
  const mine = rows.find((row) => row.userId === me);
  const before = pictureKeys.get(familyId);
  const next = new Map<string, PictureKey>();
  for (const row of rows) {
    const known = before?.get(row.userId);
    if (known?.pictureHash === row.pictureHash) {
      next.set(row.userId, known);
      continue;
    }
    // Only a record of this family and kind opens (D292).
    const envelope = await openAppRecord<{
      userId?: unknown;
      pictureHash?: unknown;
      key?: unknown;
    }>(row.sealed, familyId, 'pkey');
    // The record must say what its row says: whose, and for which picture.
    if (
      !envelope ||
      envelope.userId !== row.userId ||
      envelope.pictureHash !== row.pictureHash ||
      typeof envelope.key !== 'string'
    ) {
      continue;
    }
    const key = await importPictureKey(envelope.key);
    if (key) {
      next.set(row.userId, {
        pictureHash: row.pictureHash,
        raw: row.userId === me ? envelope.key : null,
        key,
      });
    }
  }
  // Signed out meanwhile: nothing of it stays.
  if (started !== generation) return false;
  pictureKeys.set(familyId, next);
  if (mine) {
    ownRows.set(familyId, {
      pictureHash: mine.pictureHash,
      epoch: sealedEpoch(mine.sealed),
    });
  } else {
    ownRows.delete(familyId);
  }
  loadedFamilies.add(familyId);
  const replaced: Array<{ userId: string; hash: string }> = [];
  for (const [user, entry] of before ?? []) {
    if (next.get(user)?.pictureHash !== entry.pictureHash) {
      replaced.push({ userId: user, hash: entry.pictureHash });
    }
  }
  dropStale(replaced);
  if (replaced.length > 0 || !before || before.size !== next.size) return true;
  for (const [user, entry] of next) {
    if (before.get(user) !== entry) return true;
  }
  return false;
}

/**
 * Opens and keeps a family's wrapped picture keys (D292). A read already
 * under way is shared, unless `fresh` (something changed since it began).
 */
export async function loadPictureKeys(
  familyId: string,
  { fresh = false }: { fresh?: boolean } = {}
): Promise<void> {
  listen();
  if (await refreshKeys(familyId, fresh)) notify();
}

// ---- Opening (D294) --------------------------------------------------------

interface Opened {
  url: string;
  kind: 'user' | 'family' | 'group' | 'plain';
  /** Whose picture: the user, family or chat its seal names. */
  owner: string | null;
  /** For a person's picture, the families it was opened through. */
  through: Set<string>;
}

type Fetched = { text: string } | { url: string };

const fetched = new Map<string, Promise<Fetched | null>>();
const opened = new Map<string, Opened>();
const opening = new Map<string, Promise<string | null>>();

function fetchPicture(address: string, hash: string): Promise<Fetched | null> {
  const known = fetched.get(address);
  if (known) return known;
  const request = (async (): Promise<Fetched | null> => {
    const response = await fetch(address);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const type = (response.headers.get('content-type') ?? '')
      .split(';')[0]!
      .trim();
    // A readable picture from before the cut-over (D277). This path goes
    // once the D297 sweep has run, 2026-11-12. It must be the picture its
    // address names, hashed as the server's legacy hashOf does (the
    // `data:` URL's UTF-8); never SVG.
    if (type.startsWith('image/')) {
      if (!IMAGE_TYPE.test(type)) return null;
      const body = toBase64(await response.arrayBuffer());
      const dataUrl = `data:${type};base64,${body}`;
      return (await sha256Hex32(dataUrl)) === hash ? { url: dataUrl } : null;
    }
    const text = await response.text();
    // The text must be what its address names.
    return (await pictureHash(text)) === hash ? { text } : null;
  })().catch(() => {
    // Offline or gone: try again next time.
    fetched.delete(address);
    return null;
  });
  fetched.set(address, request);
  return request;
}

function toObjectUrl(dataUrl: string): string {
  const { mime, data } = splitDataUrl(dataUrl);
  return URL.createObjectURL(new Blob([fromBase64(data)], { type: mime }));
}

/** Where a picture is shown, and whose it is meant to be. */
export interface PictureContext {
  /**
   * The family of the screen. A person's face opens only through it (D292),
   * so null shows initials for people; a family's picture must be its own.
   */
  familyId: string | null;
  /** The person, family or chat the picture is meant to be of. */
  ownerId: string;
}

/** Whether a picture already open here may be shown in this context. */
async function allowed(
  entry: Opened,
  hash: string,
  context: PictureContext,
  text: string | null
): Promise<boolean> {
  if (entry.kind === 'plain') return true;
  if (context.ownerId !== entry.owner) return false;
  if (entry.kind === 'family') {
    return !context.familyId || context.familyId === entry.owner;
  }
  if (entry.kind !== 'user') return true;
  if (!context.familyId) return false;
  const known = pictureKeys.get(context.familyId)?.get(entry.owner);
  if (known?.pictureHash !== hash) return false;
  if (entry.through.has(context.familyId)) return true;
  // A family it hasn't been opened through yet: its own key must open it.
  if (text && (await openUserPictureWith(text, known.key))) {
    entry.through.add(context.familyId);
    return true;
  }
  return false;
}

async function openSealed(
  text: string,
  hash: string,
  context: PictureContext
): Promise<{ dataUrl: string; entry: Omit<Opened, 'url'> } | null> {
  // A family's picture: a `picture` record of that family.
  if (text.startsWith(SEALED_APP)) {
    const familyId = text.slice(SEALED_APP.length, SEALED_APP.length + 36);
    if (context.familyId && context.familyId !== familyId) return null;
    if (context.ownerId !== familyId) return null;
    const envelope = await openAppRecord(text, familyId, 'picture');
    const dataUrl = pictureDataUrl(envelope);
    return dataUrl
      ? {
          dataUrl,
          entry: { kind: 'family', owner: familyId, through: new Set() },
        }
      : null;
  }
  // A group's picture: the server keeps what opens it beside it (D291).
  if (text.startsWith('{')) {
    let blob: Record<string, unknown>;
    try {
      blob = JSON.parse(text) as Record<string, unknown>;
    } catch {
      return null;
    }
    const { conversationId, clientId, keyEpoch, content } = blob ?? {};
    if (
      (blob?.v !== 1 && blob?.v !== 2) ||
      typeof conversationId !== 'string' ||
      typeof clientId !== 'string' ||
      typeof keyEpoch !== 'number' ||
      typeof content !== 'string'
    ) {
      return null;
    }
    if (context.ownerId !== conversationId) return null;
    const key = await keyFor('chat', conversationId, keyEpoch);
    if (!key) return null;
    const revision = typeof blob.revision === 'number' ? blob.revision : 0;
    // v1 only up to this device's ceiling for the chat (D328).
    if (blob.v === 1 && !(await v1Allowed('chat', conversationId, keyEpoch)))
      return null;
    // v: 2 is bound in the group domain as a picture at its revision (M76,
    // D325, D326), so no message, description or older picture can stand
    // in for it.
    const envelope = await openEnvelope<Envelope>(
      key,
      content,
      blob.v === 2
        ? groupAadV2({
            conversationId,
            clientId,
            which: 'picture',
            epoch: keyEpoch,
            revision,
          })
        : messageAad(conversationId, clientId)
    );
    if (blob.v === 2) {
      if (!admitGroupRevision(conversationId, 'picture', revision)) return null;
      await noteV2Opened('chat', conversationId, keyEpoch);
    }
    const dataUrl = pictureDataUrl(envelope);
    return dataUrl
      ? {
          dataUrl,
          entry: { kind: 'group', owner: conversationId, through: new Set() },
        }
      : null;
  }
  // A person's picture, only through the screen's family (D292).
  const record = readUserRecord(text);
  if (!record || !context.familyId) return null;
  if (context.ownerId !== record.userId) return null;
  const known = pictureKeys.get(context.familyId)?.get(record.userId);
  if (known?.pictureHash !== hash) return null;
  const dataUrl = await openUserPictureWith(text, known.key);
  return dataUrl
    ? {
        dataUrl,
        entry: {
          kind: 'user',
          owner: record.userId,
          through: new Set([context.familyId]),
        },
      }
    : null;
}

async function openNow(
  address: string,
  hash: string,
  context: PictureContext
): Promise<string | null> {
  const got = await fetchPicture(address, hash);
  if (!got) return null;
  const text = 'text' in got ? got.text : null;
  const known = opened.get(address);
  if (known) {
    return (await allowed(known, hash, context, text)) ? known.url : null;
  }
  if (!text) {
    const url = toObjectUrl((got as { url: string }).url);
    opened.set(address, {
      url,
      kind: 'plain',
      owner: null,
      through: new Set(),
    });
    return url;
  }
  const result = await openSealed(text, hash, context);
  if (!result) return null;
  // Another call may have opened it meanwhile: keep one URL per address.
  const again = opened.get(address);
  if (again) {
    return (await allowed(again, hash, context, text)) ? again.url : null;
  }
  const url = toObjectUrl(result.dataUrl);
  opened.set(address, { url, ...result.entry });
  return url;
}

/**
 * The picture at `address` (`/api/avatars/<hash>`), opened, as an object
 * URL kept for the visit (D294); null when it can't be opened: no key here
 * yet, offline, not a picture of `ownerId`, or a person's face not
 * openable through the screen's family. Never throws: the caller shows
 * initials.
 *
 * `context` is required, beyond the plan's signature: every face is drawn
 * as someone's, on some family's screen (D292).
 */
export async function openPicture(
  address: string,
  context: PictureContext
): Promise<string | null> {
  const match = ADDRESS.exec(address);
  if (!match) return null;
  const id = `${address}|${context.familyId ?? ''}|${context.ownerId}`;
  const running = opening.get(id);
  if (running) return running;
  const request = openNow(address, match[1]!, context)
    .catch(() => null)
    .finally(() => opening.delete(id));
  opening.set(id, request);
  return request;
}

// ---- Changes ---------------------------------------------------------------

const listeners = new Set<() => void>();

function notify() {
  listeners.forEach((listener) => {
    try {
      listener();
    } catch {
      // One screen's trouble isn't another's.
    }
  });
}

/** Runs `listener` when new picture keys or scope keys arrive. */
export function onPicturesChanged(listener: () => void): () => void {
  listen();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

let listening = false;

// A family key arriving may open its picture keys and picture; a chat key,
// its group picture. Faces that were initials try again (D294). Listened
// for from the first use, not on import, so pages that never show a face
// don't need the key session's events.
function listen() {
  if (listening) return;
  listening = true;
  onScopeKeysArrived((kind, id) => {
    void (async () => {
      if (kind === 'family' && loadedFamilies.has(id))
        await refreshKeys(id, true);
      notify();
    })();
  });
  onKeysArrivedQuietly(() => {
    void (async () => {
      await Promise.all([...loadedFamilies].map((id) => refreshKeys(id, true)));
      notify();
    })();
  });
}

/** Forgets every picture and key (signing out, and tests). */
export function forgetPictures() {
  for (const entry of opened.values()) URL.revokeObjectURL(entry.url);
  opened.clear();
  fetched.clear();
  opening.clear();
  pictureKeys.clear();
  ownKeys.clear();
  loadedFamilies.clear();
  ownRows.clear();
  refreshing.clear();
  rereads.clear();
  generation += 1;
}
